import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMutation } from '@/utils/api';
import { isValidUrl } from '@/utils/validation.util';

interface HealthReportResponse {
  status?: string;
  message?: string;
  grade?: string;
  score?: number;
  path?: string;
}

interface UseGenerateHealthReportReturn {
  generate: () => void;
  isGenerating: boolean;
}

/**
 * Hook to trigger on-demand health report generation.
 *
 * POST /report/health — synchronous Lambda invocation (≤30s)
 * On success: response.path contains a 7-day presigned S3 GET URL for the PDF.
 * Opens the PDF in a new tab when the URL is present.
 * Gracefully degrades: shows email-sent toast when PDF generation fails
 * (empty path).
 *
 * Browser popup-block mitigation: a blank tab is opened synchronously inside
 * the click handler (before the await) to preserve the user-gesture token.
 * The tab is navigated to the presigned URL on success, or closed on failure.
 * If window.open returns null (extension-blocked), the tab navigation is
 * skipped and the success toast is still shown.
 */
export function useGenerateHealthReport(): UseGenerateHealthReportReturn {
  const axios = useAxios();

  const mutation = useMutation<void, Error, void>({
    defaultErrorMessage: 'Failed to generate health report',
    mutationFn: async () => {
      // Open blank tab synchronously to preserve user-gesture token before await.
      const newTab = window.open('', '_blank', 'noopener,noreferrer');

      const toastId = toast.loading('Generating health report...', {
        duration: Infinity,
      });

      try {
        const response =
          await axios.post<HealthReportResponse>('/report/health');

        toast.dismiss(toastId);

        // 202 deferred: backend accepted the request but did NOT send email.
        // Inform the user honestly and do not claim the report was emailed.
        if (response.data?.status === 'accepted') {
          if (newTab) {
            newTab.close();
          }
          toast.warning(
            response.data.message ?? 'Report generation deferred.',
            { duration: 10000 }
          );
          return;
        }

        const pdfURL = response.data?.path;

        if (pdfURL && isValidUrl(pdfURL) && pdfURL.startsWith('https://')) {
          if (newTab) {
            newTab.location.href = pdfURL;
            toast.success('Health report generated', {
              description: 'PDF is loading in a new tab.',
              duration: 8000,
            });
          } else {
            toast.success('Health report ready', {
              description: 'Popup blocked. Open the report manually.',
              duration: 15000,
              action: {
                label: 'Open report',
                onClick: () =>
                  window.open(pdfURL, '_blank', 'noopener,noreferrer'),
              },
            });
          }
        } else {
          // Upload failed or URL missing — close the blank tab and show
          // the email-fallback message.
          if (newTab) {
            newTab.close();
          }
          toast.success('Risk Score Report has been emailed.');
        }
      } catch (error) {
        toast.dismiss(toastId);

        // Close the blank tab so we don't leave an orphaned empty tab.
        if (newTab) {
          newTab.close();
        }

        if (
          error instanceof Error &&
          error.message.toLowerCase().includes('already in progress')
        ) {
          toast.error('A report is already being generated. Please wait.');
        } else {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to generate health report',
            { duration: 8000 }
          );
        }

        throw error;
      }
    },
  });

  return {
    generate: mutation.mutate,
    isGenerating: mutation.isPending,
  };
}
